/**
 * AboutSeoMeta.js
 *
 * SEO + structured data component for the About section.
 * Mirrors the exact pattern of BlogSeoMeta.js / PackagesSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - BreadcrumbList JSON-LD
 *   - LocalBusiness + Person JSON-LD (appropriate for a yoga studio)
 *   - Open Graph + Twitter Card
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const CANONICAL      = `${SITE_URL}/#about`;

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Mirrors BlogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// AboutSeoMeta
// ═══════════════════════════════════════════════════════════════════════════════

export const AboutSeoMeta = () => {
  const title       = `About Us | ${SITE_NAME}`;
  const description = trimDesc(
    "Yoga Shastra cultivates physical and mental health, inner peace, and " +
    "connection with the deeper self. 8+ years of practice, 2,400+ happy " +
    "students, 4.9★ rating. Book a free trial class today."
  );
  const keywords =
    "about yoga shastra, yoga studio, yoga instructor, yoga classes, " +
    "personalised yoga, online yoga, yoga philosophy, holistic wellness";

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",  item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "About", item: CANONICAL },
    ],
  };

  // ── LocalBusiness — appropriate for a yoga studio ───────────────────────────
  const businessSchema = {
    "@context":       "https://schema.org",
    "@type":          ["LocalBusiness", "HealthAndBeautyBusiness"],
    name:              SITE_NAME,
    url:               SITE_URL,
    logo:             { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    image:             OG_IMAGE,
    description:       trimDesc(
      "Yoga Shastra integrates mind, body, and spirit through personalised " +
      "yoga instruction. We offer foundational, therapeutic, prenatal, and " +
      "holistic yoga practices for all levels."
    ),
    priceRange:        "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted:   "Cash, Credit Card, UPI",
    sameAs: [
      // Add social profile URLs here when available
    ],
    aggregateRating: {
      "@type":       "AggregateRating",
      ratingValue:   "4.9",
      bestRating:    "5",
      reviewCount:   "500",
    },
  };

  // Keyed on canonical — mirrors BlogSeoMeta so Helmet flushes correctly
  return (
    <Helmet key={CANONICAL}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={CANONICAL} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={CANONICAL} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
    </Helmet>
  );
};
